/**
 * EpTrading — EP1/EP2 交易大廳
 *
 * 4 子視圖：
 *   1) EP挂賣大廳       — GET /api/ep/orders
 *   2) 我的EP挂賣       — GET /api/ep/orders?mine=true
 *   3) 我的EP購買       — GET /api/ep/buy-records
 *   4) EP問題訂單       — GET /api/ep/orders?status=dispute
 *
 * 右上角「EP挂賣」按鈕 → /ep-sell
 * type prop / query param controls EP1 vs EP2 context.
 */
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { epAPI } from '../api/ep';
import { ApiError } from '../api/client';
import { useAuthStore } from '../store/auth';
import { EmptyState } from '../components/ui/EmptyState';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { EPTRADING_COLORS as C, COLORS, EPTRADING_TABS, TRADING_COLORS, type EpTradingTabKey } from '@/constants';
import { ThemedAvatar, ThemedFixedTopBar, ThemedPanel, ThemedStatusBadge } from '../components/ui/ThemedSurface';
import { getEpStatusLabel } from '../utils/ep-status';
import { useEpUsdtPayment } from '../hooks/useEpUsdtPayment';

/* ------------------------------------------------------------------ types */

interface EpOrder {
  id: string;
  seller_id: number;
  seller_avatar?: string | null;
  seller_name?: string;
  amount: string;
  unit_price: string;
  status: string;
  create_time: string;
  payment_methods?: string[];
  buyer_id?: number | null;
}

/* --------------------------------------------------------------- constants */

type TabKey = EpTradingTabKey;

function fmt(v: string, digits = 4): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return n.toLocaleString(undefined, { maximumFractionDigits: digits });
}

function formatOrderTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/* ---------------------------------------------------------- AvatarCircle */
// Fake avatar for order cards


function TabStrip({ tabs, value, onChange }: { tabs: { key: TabKey; label: string }[]; value: TabKey; onChange: (value: TabKey) => void }) {
  return (
    <div className="grid grid-cols-4 gap-1 ">
      {tabs.map((tab) => {
        const active = tab.key === value;
        return (
          <button
            key={tab.key}
            type="button"
            onClick={() => onChange(tab.key)}
            className="relative h-9 overflow-hidden rounded-xl px-1 text-xs font-semibold transition-all active:scale-[0.98]"
            style={{
              color: active ? C.gold : C.muted,
            }}
          >
            {active && <span className="absolute inset-x-3 bottom-0 h-px" style={{ background: C.gold }} />}
            <span className="relative block truncate">{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/* --------------------------------------------------------------- OrderCard */

function OrderCard({
  order,
  epType,
  showRedeem,
  onRedeem,
  isRedeeming,
  showBuyUsdt,
  onBuyUsdt,
  isBuyingUsdt,
  showPayUsdt,
  onPayUsdt,
  isPayingUsdt,
}: {
  order: EpOrder;
  epType: string;
  showRedeem?: boolean;
  onRedeem?: (orderId: string) => void;
  isRedeeming?: boolean;
  showBuyUsdt?: boolean;
  onBuyUsdt?: (orderId: string) => void;
  isBuyingUsdt?: boolean;
  showPayUsdt?: boolean;
  onPayUsdt?: (orderId: string) => void;
  isPayingUsdt?: boolean;
}) {
  const { t } = useTranslation();
  const acceptsUsdt = order.payment_methods?.some((m) => m.toLowerCase() === 'usdt');
  const priceUnit = acceptsUsdt ? 'USDT' : 'CNY';
  const totalValue = (Number(order.amount) * Number(order.unit_price) || 0).toLocaleString(undefined, {
    maximumFractionDigits: acceptsUsdt ? 4 : 2,
  });
  const canRedeem = showRedeem && order.status === 'open' && onRedeem;
  const canBuyUsdt = showBuyUsdt && order.status === 'open' && acceptsUsdt && onBuyUsdt;
  const canPayUsdt = showPayUsdt && order.status === 'claimed' && acceptsUsdt && onPayUsdt;

  return (
    <ThemedPanel theme={C} className="p-0">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent 0%, ${C.activeGreen} 50%, transparent 100%)` }} />
      <div className="px-4 py-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <ThemedAvatar seed={order.id} />
            <div className="min-w-0 flex flex-col gap-1">
              <div className="font-mono text-xs font-semibold leading-none" style={{ color: C.text }}>#{order.id.slice(0, 16)}...</div>
              <div className="text-[10px]" style={{ color: C.muted }}>{formatOrderTime(order.create_time)}</div>
            </div>
          </div>
          <ThemedStatusBadge label={getEpStatusLabel(order.status, t)} />
        </div>

        <div className="mt-4 grid grid-cols-[1fr_auto_1fr] items-center gap-3 rounded-xl border px-3 py-3" style={{ borderColor: C.softBorder, background: C.inputBg }}>
          <div className="min-w-0">
            <div className="text-[10px] leading-none" style={{ color: C.muted }}>{t('common.amount')}</div>
            <div className="mt-1 truncate font-mono text-lg font-semibold leading-none" style={{ color: C.gold }}>
              {fmt(order.amount)}
              <span className="ml-1 text-xs" style={{ color: C.muted }}>{epType}</span>
            </div>
          </div>
          <div className="h-10 w-px" style={{ background: C.statDividerAlt }} />
          <div className="min-w-0 text-right">
            <div className="text-[10px] leading-none" style={{ color: C.muted }}>{t('common.unitPrice')}</div>
            <div className="mt-1 truncate font-mono text-lg font-semibold leading-none" style={{ color: C.blue }}>
              {fmt(order.unit_price)}
              <span className="ml-1 text-xs" style={{ color: C.muted }}>{priceUnit}</span>
            </div>
          </div>
        </div>

        <div className="mt-2 flex items-center justify-between rounded-lg border px-3 py-2" style={{ borderColor: C.line, background: `linear-gradient(90deg, transparent 0%, ${C.green}10 100%)` }}>
          <span className="text-[10px] leading-none" style={{ color: C.muted }}>{t('common.value')}</span>
          <span className="font-mono text-sm font-semibold" style={{ color: C.green }}>
            {totalValue} {priceUnit}
          </span>
        </div>

        {canBuyUsdt && (
          <button
            type="button"
            disabled={isBuyingUsdt}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (isBuyingUsdt) return;
              onBuyUsdt(order.id);
            }}
            className="mt-3 w-full rounded-xl border py-2.5 text-sm font-semibold transition-all active:scale-[0.98] disabled:opacity-60"
            style={{
              borderColor: `${C.blue}55`,
              color: C.blue,
              background: `${C.blue}12`,
            }}
          >
            {isBuyingUsdt ? t('common.processing', '处理中…') : t('epTrading.buyUsdt', 'USDT购买')}
          </button>
        )}

        {canPayUsdt && (
          <button
            type="button"
            disabled={isPayingUsdt}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (isPayingUsdt) return;
              onPayUsdt(order.id);
            }}
            className="mt-3 w-full rounded-xl border py-2.5 text-sm font-semibold transition-all active:scale-[0.98] disabled:opacity-60"
            style={{
              borderColor: `${C.blue}55`,
              color: C.blue,
              background: `${C.blue}12`,
            }}
          >
            {isPayingUsdt ? t('common.processing', '处理中…') : t('epTrading.payUsdt', 'USDT付款')}
          </button>
        )}

        {canRedeem && (
          <button
            type="button"
            disabled={isRedeeming}
            onClick={() => onRedeem(order.id)}
            className="mt-3 w-full rounded-xl border py-2.5 text-sm font-semibold transition-all active:scale-[0.98] disabled:opacity-60"
            style={{
              borderColor: `${C.gold}55`,
              color: C.gold,
              background: `${C.gold}12`,
            }}
          >
            {isRedeeming ? t('common.processing', '处理中…') : t('epTrading.redeemOrder', '赎回')}
          </button>
        )}
      </div>
    </ThemedPanel>
  );
}

/* ---------------------------------------------------------------- Filter */

function FilterBar({
  epType,
  minVal,
  maxVal,
  onMinChange,
  onMaxChange,
}: {
  epType: string;
  minVal: string;
  maxVal: string;
  onMinChange: (v: string) => void;
  onMaxChange: (v: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="pt-3">
      <div className="flex items-center gap-2">
        <span className="shrink-0 text-xs" style={{ color: C.muted }}>{t('epTrading.filter')}</span>
          <div className="flex min-w-0 flex-1 items-center gap-1.5">
            <input
              inputMode="numeric"
              className="min-w-0 flex-1 rounded-lg border p-2 text-center text-xs font-mono border-[#F5EC63]/30 bg-black/35 font-semibold outline-none placeholder:text-white/50 focus:border-[#ADD741]" style={{ color: COLORS.iconGlobal }}
              placeholder="0"
              value={minVal}
            onChange={(e) => onMinChange(e.target.value)}
          />
          <span className="text-xs" style={{ color: C.muted }}>{t('epTrading.filterTo')}</span>
          <input
            inputMode="numeric"
            className="min-w-0 flex-1 rounded-lg border p-2 text-center text-xs font-mono border-[#F5EC63]/30 bg-black/35 font-semibold outline-none placeholder:text-white/50 focus:border-[#ADD741]" style={{ color: COLORS.iconGlobal }}
            placeholder="3000"
            value={maxVal}
            onChange={(e) => onMaxChange(e.target.value)}
          />
          <span className="shrink-0 text-xs" style={{ color: C.muted }}>{epType}</span>
        </div>
      </div>
    </div>
  );
}

function InfoBanner({ epType }: { epType: string }) {
  const { t } = useTranslation();

  return (
    <div className="pb-1">
      <ThemedPanel theme={C} className="p-4">
        <div className="text-xs leading-relaxed" style={{ color: C.muted }}>
          <span className="font-semibold" style={{ color: C.gold }}>#{t('epTrading.hint')}#</span>{' '}
          1. {t('epTrading.rules1', { epType })}
          <br />
          2. {t('epTrading.rules2')}
          <br />
          3. {t('epTrading.rules3')}
        </div>
      </ThemedPanel>
    </div>
  );
}

function FixedTradingControls({
  epType,
  tabs,
  activeTab,
  filterMin,
  filterMax,
  onTabChange,
  onMinChange,
  onMaxChange,
}: {
  epType: string;
  tabs: { key: TabKey; label: string }[];
  activeTab: TabKey;
  filterMin: string;
  filterMax: string;
  onTabChange: (tab: TabKey) => void;
  onMinChange: (value: string) => void;
  onMaxChange: (value: string) => void;
}) {
  return (
    <ThemedFixedTopBar background={TRADING_COLORS.fixedTopBg} className="px-0 py-0 pb-3">
      <InfoBanner epType={epType} />
      <TabStrip tabs={tabs} value={activeTab} onChange={onTabChange} />
      {activeTab === 'hall' && (
        <FilterBar
          epType={epType}
          minVal={filterMin}
          maxVal={filterMax}
          onMinChange={onMinChange}
          onMaxChange={onMaxChange}
        />
      )}
    </ThemedFixedTopBar>
  );
}

function LoadingCards() {
  return (
    <>
      <LoadingSkeleton className="h-20" />
      <LoadingSkeleton className="h-20" />
      <LoadingSkeleton className="h-20" />
    </>
  );
}

function OrderList({
  orders,
  emptyTitle,
  epType,
  showRedeem,
  onRedeem,
  redeemingId,
  showBuyUsdt,
  onBuyUsdt,
  buyingUsdtId,
  showPayUsdt,
  onPayUsdt,
  payingUsdtId,
  currentUserId,
}: {
  orders?: EpOrder[];
  emptyTitle: string;
  epType: string;
  showRedeem?: boolean;
  onRedeem?: (orderId: string) => void;
  redeemingId?: string | null;
  showBuyUsdt?: boolean;
  onBuyUsdt?: (orderId: string) => void;
  buyingUsdtId?: string | null;
  showPayUsdt?: boolean;
  onPayUsdt?: (orderId: string) => void;
  payingUsdtId?: string | null;
  currentUserId?: number;
}) {
  const rows = orders ?? [];
  if (rows.length === 0) return <EmptyState title={emptyTitle} />;
  return (
    <>
      {rows.map((order) => (
        <OrderCard
          key={order.id}
          order={order}
          epType={epType}
          showRedeem={showRedeem}
          onRedeem={onRedeem}
          isRedeeming={redeemingId === order.id}
          showBuyUsdt={showBuyUsdt && order.seller_id !== currentUserId}
          onBuyUsdt={onBuyUsdt}
          isBuyingUsdt={buyingUsdtId === order.id}
          showPayUsdt={showPayUsdt}
          onPayUsdt={onPayUsdt}
          isPayingUsdt={payingUsdtId === order.id}
        />
      ))}
    </>
  );
}

/* =================================================================== Page */

export function EpTrading() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const epType = searchParams.get('type') === 'ep2' ? 'EP2' : 'EP1';
  const userId = useAuthStore((s) => s.profile?.id);

  const [activeTab, setActiveTab] = useState<TabKey>('hall');
  const [filterMin, setFilterMin] = useState('');
  const [filterMax, setFilterMax] = useState('');
  const [redeemingId, setRedeemingId] = useState<string | null>(null);
  const usdtPay = useEpUsdtPayment();

  const handleUsdtPay = async (orderId: string) => {
    if (usdtPay.isBusy) return;
    try {
      const hash = await usdtPay.payWithUsdt(orderId);
      void qc.invalidateQueries({ queryKey: ['ep'] });
      if (!hash) {
        if (usdtPay.error) {
          queueMicrotask(() => alert(usdtPay.error));
        }
        return;
      }
      setActiveTab('my-buy');
      queueMicrotask(() =>
        alert(t('epTrading.usdtPaySuccess', 'USDT已支付，EP已自动到账')),
      );
    } catch (e) {
      void qc.invalidateQueries({ queryKey: ['ep'] });
      setActiveTab('my-buy');
      const msg =
        e instanceof Error && e.message
          ? e.message
          : usdtPay.error || t('epTrading.usdtPayFailed', 'USDT支付失败');
      queueMicrotask(() => alert(msg));
    }
  };

  const redeem = useMutation({
    mutationFn: (order_id: string) => epAPI.cancel({ order_id }),
    onMutate: (order_id) => setRedeemingId(order_id),
    onSettled: () => setRedeemingId(null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ep'] });
      alert(t('epTrading.redeemSuccess', '赎回成功'));
    },
    onError: (err) => {
      alert(err instanceof ApiError ? err.message : t('epTrading.redeemFailed', '赎回失败'));
    },
  });

  const tabs = EPTRADING_TABS.map((tab) => ({ ...tab, label: t(tab.labelKey) }));

  /* ---- hall orders ---- */
  const epTypeKey = epType === 'EP2' ? 'ep2' : 'ep1';

  const { data: hallOrders, isLoading: loadingHall } = useQuery({
    queryKey: ['ep', 'orders', 'hall', epTypeKey, filterMin, filterMax],
    queryFn: () =>
      epAPI.listOrders({
        status: 'open',
        ep_type: epTypeKey,
        min_amount: filterMin || undefined,
        max_amount: filterMax || undefined,
        page: 1,
        limit: 50,
      }),
    enabled: activeTab === 'hall',
    refetchInterval: 10_000,
  });

  const { data: mySellOrders, isLoading: loadingMySell } = useQuery({
    queryKey: ['ep', 'orders', 'seller', userId],
    queryFn: () => epAPI.listOrders({ seller_id: userId!, page: 1, limit: 50 }),
    enabled: activeTab === 'my-sell' && typeof userId === 'number',
    refetchInterval: 10_000,
  });

  const { data: myBuyRecords, isLoading: loadingMyBuy } = useQuery({
    queryKey: ['ep', 'orders', 'buyer', userId],
    queryFn: () => epAPI.listOrders({ buyer_id: userId!, page: 1, limit: 50 }),
    enabled: activeTab === 'my-buy' && typeof userId === 'number',
    refetchInterval: 10_000,
  });

  const { data: disputeOrders, isLoading: loadingDispute } = useQuery({
    queryKey: ['ep', 'orders', 'disputed'],
    queryFn: () => epAPI.listOrders({ status: 'disputed', page: 1, limit: 50 }),
    enabled: activeTab === 'dispute',
    refetchInterval: 10_000,
  });

  const isLoading =
    (activeTab === 'hall' && loadingHall) ||
    (activeTab === 'my-sell' && loadingMySell) ||
    (activeTab === 'my-buy' && loadingMyBuy) ||
    (activeTab === 'dispute' && loadingDispute);

  return (
    <div className="relative min-h-full pb-8" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader
          title={epType === 'EP2' ? t('epTrading.titleEp2') : t('epTrading.title')}
          action={`${epType} ${t('epTrading.sellAction')}`}
          onBack={() => navigate(-1)}
          onAction={() => navigate('/ep-sell')}
          theme={C}
        />

        <FixedTradingControls
          epType={epType}
          tabs={tabs}
          activeTab={activeTab}
          filterMin={filterMin}
          filterMax={filterMax}
          onTabChange={setActiveTab}
          onMinChange={setFilterMin}
          onMaxChange={setFilterMax}
        />

        <main className="px-5 pb-5">
          <div className={`space-y-4 ${activeTab === 'hall' ? 'pt-[295px]' : 'pt-[254px]'}`}>
            {isLoading && <LoadingCards />}
            {activeTab === 'hall' && !loadingHall && (
              <OrderList
                orders={hallOrders?.rows}
                emptyTitle={t('epTrading.noOrders')}
                epType={epType}
                showBuyUsdt
                onBuyUsdt={handleUsdtPay}
                buyingUsdtId={usdtPay.isBusy ? usdtPay.activeOrderId : null}
                currentUserId={userId}
              />
            )}
            {activeTab === 'my-sell' && !loadingMySell && (
              <OrderList
                orders={mySellOrders?.rows}
                emptyTitle={t('epTrading.noMySell')}
                epType={epType}
                showRedeem
                onRedeem={(orderId) => redeem.mutate(orderId)}
                redeemingId={redeemingId}
                currentUserId={userId}
              />
            )}
            {activeTab === 'my-buy' && !loadingMyBuy && (
              <OrderList
                orders={myBuyRecords?.rows}
                emptyTitle={t('epTrading.noMyBuy')}
                epType={epType}
                showPayUsdt
                onPayUsdt={handleUsdtPay}
                payingUsdtId={usdtPay.isBusy ? usdtPay.activeOrderId : null}
                currentUserId={userId}
              />
            )}
            {activeTab === 'dispute' && !loadingDispute && (
              <OrderList orders={disputeOrders?.rows} emptyTitle={t('epTrading.noDispute')} epType={epType} />
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default EpTrading;
